import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { notificationEndpointsApi } from '../services/api';
import Layout from '../components/layout/Layout';
import type { AuthState } from '../types';

interface VerifyEndpointProps {
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
}

const VerifyEndpoint: React.FC<VerifyEndpointProps> = ({ authState, setAuthState }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }
    notificationEndpointsApi.verifyEndpoint(token)
      .then(res => {
        if (res.success) {
          setStatus('success');
          setMessage('Thank you for verifying your email.\n\nYou will now receive stock alert notifications from MarketEye when the rules you have set are triggered.');
        } else {
          setStatus('error');
          setMessage(res.error || 'Verification failed.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Verification failed. The link may be invalid or expired.');
      });
  }, [searchParams]);

  return (
    <Layout authState={authState} setAuthState={setAuthState}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            {status === 'pending' && (
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-blue-500 animate-spin mb-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-700">{message}</p>
              </div>
            )}
            {status === 'success' && (
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-green-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707a1 1 0 00-1.414-1.414L9 11.586 7.707 10.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-green-700 font-semibold mb-2">{message}</p>
                <Link to="/settings" className="text-blue-600 hover:underline">Go to Notification Settings</Link>
              </div>
            )}
            {status === 'error' && (
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5a1 1 0 112 0 1 1 0 01-2 0zm.293-7.707a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 7.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-red-700 font-semibold mb-2">{message}</p>
                <Link to="/settings" className="text-blue-600 hover:underline">Go to Notification Settings</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VerifyEndpoint; 